import { MediaImageStudioMode } from '@wix/media-image-studio-opener/dist/src';
import classnames from 'classnames';
import { contained } from 'ramda-adjunct';
import React, { FC, memo, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { TrackBoardClickTarget, useTrackBoardAnalytics } from '../../../utils/hooks/bi/useTrackBoardAnalytics';
import {
  ImageEditToolClickOrigin,
  ImageEditToolTarget,
  useTrackImageEditToolClick,
} from '../../../utils/hooks/bi/useTrackImageEditToolClick';
import { useBoards } from '../../../utils/hooks/useBoards';
import { useBoolean } from '../../../utils/hooks/useBoolean';
import { useGetItemPrice } from '../../../utils/hooks/useGetItemPrice';
import { useMediaStudio } from '../../../utils/hooks/useMediaStudio';
import { useOpenContextMenu } from '../../../utils/hooks/useOpenContextMenu';
import { useSources } from '../../../utils/hooks/useSources';
import { MediaManagerSDKFile } from '../../../utils/media-manager-sdk';
import {
  canItemBeExpandedFromActions,
  canItemBeShared,
  canOpenItemInImageEditor,
} from '../../../utils/media-manager-sdk/items';
import { isTranscoding } from '../../../utils/media-manager-sdk/transcoding';
import { BoardsContextMenuTrigger } from '../BoardsContextMenu';
import { Button, Popover, PopoverMenu, Tooltip, WixIcons } from '../index';
import styles from './ItemActionsRow.scss';
import { ActionButton, ActionButtonSkin, IOuter } from './ItemActionsRow.types';
import { PurchaseButtonWrapper } from './PurchaseButtonWrapper';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { SystemFolderId } from '../../../constants/SystemFolderId';
import { ItemActionButton } from './ItemActionButton/ItemActionButton';
import { useIntersectionObserver } from '../../../utils/hooks/useIntersectionObserver';

const ItemActionsRowComponent: FC<IOuter> = ({
  className,
  onThreeDotsClick,
  item,
  buttons,
  onBuyClick,
  onOpenExpandedViewClick,
  skin = ActionButtonSkin.Dark,
  isPanel,
  innerClassName,
  currentFolder,
  onRemoveItemsFromBoard,
  source,
  actionsClickOrigin,
}) => {
  const { trackImageEditToolClick } = useTrackImageEditToolClick();
  const { getItemPrice, getTaxSettings } = useGetItemPrice();

  const price = getItemPrice(item);
  const tax = getTaxSettings(item);

  const {
    mmgrSettings: { team, hideCreateAndEditActions },
  } = useMMGRSettings();

  const shouldDisableEditActions = isTranscoding(item);

  const canDisplayButton = useCallback(contained(buttons), [buttons]);

  const buyThisItem = useCallback(() => onBuyClick(item as MediaManagerSDKFile), [onBuyClick, item]);

  const canDisplayEditUnsplashAction = canOpenItemInImageEditor(item);

  const canDisplayCropAndEditButton =
    canDisplayButton(ActionButton.CropAndEdit) &&
    (canOpenItemInImageEditor(item) || canDisplayEditUnsplashAction) &&
    !hideCreateAndEditActions &&
    !shouldDisableEditActions;

  const canDisplayPreviewButton = canDisplayButton(ActionButton.Preview) && canItemBeExpandedFromActions(item);

  const { openMediaStudio } = useMediaStudio();
  const editItemInCropAndRotateMode = useCallback(
    () =>
      openMediaStudio({
        file: item as MediaManagerSDKFile,
        mode: MediaImageStudioMode.Transform,
        targetFolder: currentFolder,
      }),
    [openMediaStudio, item, currentFolder],
  );

  const handleCropAndEditClick = useCallback(() => {
    trackImageEditToolClick(
      ImageEditToolTarget.CropAndRotate,
      isPanel ? ImageEditToolClickOrigin.Preview : ImageEditToolClickOrigin.ImageThumbnails,
      item,
    );
    editItemInCropAndRotateMode();
  }, [editItemInCropAndRotateMode, item, isPanel]);

  const boardsContextMenuTriggerRef = useRef();
  const openBoardsContextMenu = useOpenContextMenu(boardsContextMenuTriggerRef);

  const { trackBoardClick } = useTrackBoardAnalytics();

  const { boards, addItems } = useBoards();
  const hasOnlyFavoritesBoard = boards.length === 1;

  const handleAddToBoardClick = useCallback(
    (event) => {
      if (hasOnlyFavoritesBoard) {
        addItems(boards, [item], { clickOrigin: actionsClickOrigin });
      } else {
        openBoardsContextMenu(event);
      }

      trackBoardClick(item, {
        target: TrackBoardClickTarget.Add,
        clickOrigin: actionsClickOrigin,
      });
    },
    [addItems, item, boards, openBoardsContextMenu, trackBoardClick, actionsClickOrigin, hasOnlyFavoritesBoard],
  );

  const handleRemoveSelectedItemsFromBoard = useCallback(() => {
    onRemoveItemsFromBoard([item], { clickOrigin: actionsClickOrigin });

    trackBoardClick(item, {
      target: TrackBoardClickTarget.Remove,
      clickOrigin: actionsClickOrigin,
    });
  }, [actionsClickOrigin, item, onRemoveItemsFromBoard, trackBoardClick]);

  const { isBoardRoot, isTrash } = useSources({ source });
  const shouldShowAddToBoardButton =
    canItemBeShared(item, team) &&
    canDisplayButton(ActionButton.AddToBoard) &&
    !isBoardRoot &&
    !isTrash &&
    !(item.id === SystemFolderId.WixVideoLink);
  const shouldShowRemoveFromBoardButton = canDisplayButton(ActionButton.AddToBoard) && isBoardRoot && !isTrash;

  const buttonsMap = {
    cropAndEdit: canDisplayCropAndEditButton && (
      <Tooltip content={<FormattedMessage id="actions.crop-and-edit" />} maxWidth={230} appendTo="window">
        <span className={innerClassName}>
          <ItemActionButton skin={skin} isIcon onClick={handleCropAndEditClick} dataHook="crop-and-edit-action-button">
            <WixIcons.Crop />
          </ItemActionButton>
        </span>
      </Tooltip>
    ),

    preview: canDisplayPreviewButton && (
      <Tooltip content={<FormattedMessage id="actions.expand" />} maxWidth={230} appendTo="window">
        <span className={innerClassName}>
          <ItemActionButton skin={skin} isIcon onClick={onOpenExpandedViewClick} dataHook="preview-button">
            <WixIcons.Preview />
          </ItemActionButton>
        </span>
      </Tooltip>
    ),

    addToBoard: shouldShowAddToBoardButton && (
      <Tooltip
        content={
          hasOnlyFavoritesBoard ? (
            <FormattedMessage id="actions.add-to-favs-board" />
          ) : (
            <FormattedMessage id="actions.add-to-board" />
          )
        }
        maxWidth={230}
        appendTo="window"
      >
        <BoardsContextMenuTrigger contextMenuRef={boardsContextMenuTriggerRef} clickOrigin={actionsClickOrigin}>
          <ItemActionButton skin={skin} onClick={handleAddToBoardClick} dataHook="add-to-board-button">
            <WixIcons.Bookmark />
          </ItemActionButton>
        </BoardsContextMenuTrigger>
      </Tooltip>
    ),

    removeFromBoard: shouldShowRemoveFromBoardButton && (
      <Tooltip content={<FormattedMessage id="actions.remove-from-board" />} maxWidth={230} appendTo="window">
        <ItemActionButton skin={skin} onClick={handleRemoveSelectedItemsFromBoard} dataHook="remove-from-board-button">
          <WixIcons.BookmarkFilled />
        </ItemActionButton>
      </Tooltip>
    ),

    purchase: canDisplayButton(ActionButton.Purchase) && price && (
      <PurchaseButtonWrapper showPricesWithTax={tax?.showPricesWithTax} displayTaxType={tax?.displayTaxType}>
        <Button
          className={styles.actionButton}
          size="tiny"
          skin="standard"
          onClick={buyThisItem}
          dataHook="purchase-button"
        >
          {price}
        </Button>
      </PurchaseButtonWrapper>
    ),

    more: canDisplayButton(ActionButton.More) && (
      <ItemActionButton skin={skin} isIcon onClick={onThreeDotsClick} dataHook="three-dots">
        <WixIcons.MoreSmall />
      </ItemActionButton>
    ),
  };

  const [rowTriggerRef, rowTriggerEntry] = useIntersectionObserver();

  const shouldDisplayActionsSeparately =
    true || rowTriggerEntry?.isIntersecting || canDisplayButton(ActionButton.More) || !buttonsMap.purchase;

  const { value: areCombinedActionsOpen, setFalse: hideCombinedActions, toggle: toggleCombinedActions } = useBoolean();

  const handlePreviewClickInPopoverMenu = useCallback(() => {
    hideCombinedActions();
    onOpenExpandedViewClick();
  }, [hideCombinedActions, onOpenExpandedViewClick]);

  return (
    <>
      <div className={classnames(styles.newActions, className)} data-hook="item-actions-row">
        {shouldDisplayActionsSeparately ? (
          <>
            {buttonsMap.cropAndEdit}
            {buttonsMap.preview}
            {buttonsMap.addToBoard}
            {buttonsMap.removeFromBoard}
          </>
        ) : (
          <Popover
            className={styles.actionButton}
            shown={areCombinedActionsOpen}
            placement="top"
            appendTo={document.body}
            onClickOutside={hideCombinedActions}
            showDelay={0}
            showArrow
          >
            <Popover.Element>
              <ItemActionButton skin={skin} isIcon onClick={toggleCombinedActions} dataHook="three-dots">
                <WixIcons.MoreSmall />
              </ItemActionButton>
            </Popover.Element>
            <Popover.Content>
              <div className={styles.combinedActions}>
                <PopoverMenu.MenuItem
                  prefixIcon={<WixIcons.Preview />}
                  text={<FormattedMessage id="actions.expand" />}
                  onClick={handlePreviewClickInPopoverMenu}
                />
              </div>
            </Popover.Content>
          </Popover>
        )}

        {buttonsMap.purchase}
        {buttonsMap.more}
      </div>

      <div className={classnames(styles.newActions, styles.rowWidthTriggerWrapper)}>
        <div className={styles.rowWidthTrigger} ref={rowTriggerRef} />

        {buttonsMap.preview}
        {buttonsMap.purchase}
      </div>
    </>
  );
};

export const ItemActionsRow: FC<IOuter> = memo<IOuter>(ItemActionsRowComponent);
